import { inject } from 'aurelia';
import { SchedulerApi } from '../../../apis/scheduler/scheduler-api';
import { IRouter } from '@aurelia/router';
import { SchedulerModel } from '../../../model/scheduler-model';
import { Message } from '../../../components/callout/message';
import { SerieSettingEntity } from '../../../apis/serie/serie-setting-entity';
import { TeamSettingEntity } from '../../../apis/team/team-setting-entity';
import { SchedulerSetting } from '../../../components/scheduler/scheduler-setting';
import { SchedulerCreateEntity } from '../../../apis/scheduler/scheduler-create-entity';
import { SchedulerResponseEntity } from '../../../apis/scheduler/scheduler-response-entity';
import { MessageType } from '../../../components/callout/message-type';
import { HttpErrorHelper } from '../../../infrastructure/error/http-error-helper';
import { I18N } from '@aurelia/i18n';
import { ScheduleRowCollection } from '../../../model/schedule-row-collection';
import { CustomIntervalEntity } from '../../../apis/custom-interval/custom-interval-entity';
import { VenueEntity } from '../../../apis/venue/venue-entity';
import { EditScheduleViewModel } from './edit-schedule-view-model';
import { SchedulerModelService } from '../../../services/scheduler-model-service';
import { ProjectModelService } from '../../../services/project-model-service';
import { GroupEntity } from '../../../apis/group/group-entity';

@inject(SchedulerApi, IRouter, SchedulerModelService, ProjectModelService, HttpErrorHelper, I18N)
export class EditSchedule {

    public messages: Message[] = [];
    public viewModel: EditScheduleViewModel = null;
    public editModeIndex: number | null = null;
    private _isBusy: boolean = false;
    private _isInitializing: boolean = false;
    private _pendingGeneration: boolean = false;
    private _addCreatedMessage: boolean = false;
    private _schedulerModel: SchedulerModel = null;
    private _schedulerApi: SchedulerApi = null;
    private _router: IRouter = null;
    private _httpErrorHelper: HttpErrorHelper = null;
    private _i18n: I18N = null;
    private _schedulerModelService: SchedulerModelService;
    private _projectModelService: ProjectModelService;
    constructor(schedulerApi: SchedulerApi, router: IRouter, schedulerModelService: SchedulerModelService, projectModelService: ProjectModelService, httpErrorHelper: HttpErrorHelper, i18n: I18N) {
        this._schedulerApi = schedulerApi;
        this._router = router;
        this._schedulerModelService = schedulerModelService;
        this._projectModelService = projectModelService;
        this._httpErrorHelper = httpErrorHelper;
        this._i18n = i18n;
        // this.viewModel = new EditScheduleViewModel(this._schedulerModel.games, this.serie, this.customIntervals, this.teamSettings, this.venues);
    }

    public canLoad(): boolean {
        if (!this._schedulerModelService.isLoaded) {
            setTimeout(() => void this._router.load('create'), 0);
            return false;
        }
        return true;
    }

    // Generation runs in attached() so the page (with its spinner) is shown instead of the router waiting on the previous page.
    public loading(): void {
        this._isBusy = false;
        this._schedulerModel = this._schedulerModelService.model;
        this._addCreatedMessage = this._schedulerModel.scheduleRowCollections.any(s => s.isSaved) && this._schedulerModel.hasBreakingChanges;

        if (!this.hasSchedules) {
            this._schedulerModel.clearSchedules(true);
            this._startGeneration();
        } else if (this._schedulerModel.hasBreakingChanges) {
            // Existing (saved) schedules get view models now so the page can render while the new one is generated.
            for (const schedule of this.schedules) {
                this._viewModels.add(new EditScheduleViewModel(schedule.games, this._schedulerModel.serie, this._schedulerModel.customIntervals, this._schedulerModel.teamSettings, this.venues, this.groups));
            }
            this._startGeneration();
        } else {
            if (this._viewModels.length === 0 && this.schedules.length > 0) {
                // Reload after navigating back: rebuild a view model for every schedule
                for (const schedule of this.schedules) {
                    const vm = new EditScheduleViewModel(schedule.games, this._schedulerModel.serie, this._schedulerModel.customIntervals, this._schedulerModel.teamSettings, this.venues, this.groups);
                    this._viewModels.add(vm);
                }
                const selectedIndex = this.schedules.findIndex(s => s.selected);
                this.selectSchedule(selectedIndex >= 0 ? selectedIndex : 0);
            }
            this._schedulerModel.hasBreakingChanges = false;
            this._showCreatedMessageIfNeeded();
        }
    }

    public async attached(): Promise<void> {
        if (!this._pendingGeneration) {
            return;
        }
        this._pendingGeneration = false;
        try {
            await this.createSchedule();
        } finally {
            this._isInitializing = false;
        }
        this._schedulerModel.hasBreakingChanges = false;
        this._showCreatedMessageIfNeeded();
    }

    private _startGeneration(): void {
        this._pendingGeneration = true;
        this._isInitializing = true;
        this._isBusy = true;
    }

    private _showCreatedMessageIfNeeded(): void {
        if (this._addCreatedMessage) {
            this._addCreatedMessage = false;
            this._addMessage(this._i18n.tr('ui:save_schedule_exists_info'), MessageType.danger);
        }
    }

    public get isInitializing(): boolean {
        return this._isInitializing;
    }
    public get scheduleId(): string {
        return this._schedulerModel.scheduleId;
    }

    public get schedules(): ScheduleRowCollection[] {
        return this._schedulerModel.scheduleRowCollections;
    }

    public get serie(): SerieSettingEntity {
        return this._schedulerModel.serie;
    }

    public get teamSettings(): TeamSettingEntity[] {
        return this._schedulerModel.teamSettings;
    }

    public get customIntervals(): CustomIntervalEntity[] {
        return this._schedulerModel.customIntervals;
    }

    public get hasMultipleGroups(): boolean {
        return this._schedulerModel?.groupIds?.length > 1;
    }

    public get schedulerSetting(): SchedulerSetting {
        return this._schedulerModel.schedulerSetting;
    }

    public get venues(): VenueEntity[] {
        return this._projectModelService.getVenuesForEvent();
    }

    public get groups(): GroupEntity[] {
        return this._projectModelService.currentModel?.groups ?? [];
    }

    public selectSchedule(index: number): void {
        this._schedulerModel.selectScheduleRowCollection(index);
        if (this._viewModels[index] != null) {
            this.viewModel = this._viewModels[index];
        }
    }

    private _viewModels: EditScheduleViewModel[] = [];
    public getModel(index: number): EditScheduleViewModel {
        return this._viewModels[index];

    }

    public async createSchedule(): Promise<void> {
        this._isBusy = true;
        if (!this.hasSchedules) {
            this.schedules.clear();
        }
        const dto = new SchedulerCreateEntity();
        dto.maxExtraIntervals = this.schedulerSetting.maxExtraIntervals;
        dto.allowGamesInAdjacentIntervals = this.schedulerSetting.allowGamesInAdjacentIntervals;
        dto.teamDistributionStrategy = this.schedulerSetting.teamDistributionStrategy;
        dto.fields = this.serie.fields;
        dto.gamePause = this.serie.gamePause;
        dto.periodLength = this.serie.periodLength;
        dto.periodPause = this.serie.periodPause;
        dto.periods = this.serie.periods;
        // The scheduler engine takes a concrete per-team count; resolve each team's override
        // (or the series default) here so callers of the API never send a null gameCount.
        dto.teamSettings = this.teamSettings.map(t => {
            const resolved = TeamSettingEntity.fromDto(t.toDto());
            resolved.gameCount = t.getEffectiveGameCount(this.serie.gameCount);
            return resolved;
        });
        dto.startTime = this.serie.startTime;
        try {
            const schedule1 = await this._schedulerApi.create(dto);

            const viewModel = new EditScheduleViewModel(schedule1.games, this._schedulerModel.serie, this._schedulerModel.customIntervals, this._schedulerModel.teamSettings, [], this.groups);
            this._viewModels.unshift(viewModel);
            this.addScheduleToList(schedule1);
            this.selectSchedule(0);
        } catch (err) {
            const apiError = err as { statusCode?: number };
            if (apiError?.statusCode === 404) {
                this._addMessage(this._i18n.tr('ui:no_schedule_found'), MessageType.danger);
            } else {
                const errors = this._httpErrorHelper.getTranslatedHttpErrors(err);
                for (const error of errors) {
                    this._addMessage(error.message, MessageType.danger);
                }
            }
        }
        this._isBusy = false;
    }

    // public hasReminder(): boolean {  unused???
    //   const gameCount = this.teamSettings.sum(t => t.gameCount);
    //   return gameCount % 2 > 0;
    // }
    public onDeleteSchedule(index: number, event: any): boolean {
        this.schedules.splice(index, 1);
        if (this.schedules.length > 0 && !this.schedules.any(s => s.selected)) {
            this.selectSchedule(0);
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        event.stopPropagation();
        return false;
    }

    public addScheduleToList(scheduleToAdd: SchedulerResponseEntity): void {
        this._schedulerModel.addSchedulerResponse(scheduleToAdd);
    }
    public get isBusy(): boolean {
        return this._isBusy;
    }
    public get hasSchedules(): boolean {
        return this.schedules.any();
    }
    public get isValid(): boolean {
        return this.hasSchedules && this.schedules.any(s => s.selected === true);
    }

    public toggleEditMode(index: number): void {
        this.editModeIndex = this.editModeIndex === index ? null : index;
    }

    public get showCancel(): boolean {
        return true;
    }

    public previous(): void {
        this._isBusy = true;
        void this._router.load(`create/edit-serie/${this._schedulerModel.eventId}/${this._schedulerModel.scheduleId}`);
    }

    public next(): void {
        this._isBusy = true;
        const scheduleRows = this.viewModel.getEditedScheduleRows();
        this._schedulerModel.selectedScheduleRowCollection.updateGames(scheduleRows);
        void this._router.load('create/customize-schedule');
    }

    public finish(): void {
        this._isBusy = true;
        const scheduleRows = this.viewModel.getEditedScheduleRows();
        this._schedulerModel.selectedScheduleRowCollection.updateGames(scheduleRows);
        this._projectModelService.finalizeSchedule(this._schedulerModel);
        void this._router.load(`create/event-summary/${this._schedulerModel.eventId}`);
    }

    public cancel(): void {
        this._isBusy = true;
        void this._router.load(`create/event-summary/${this._schedulerModel.eventId}`);
    }

    // private _clearMessages(): void {
    //   this.messages.clear();
    // }
    private _addMessage(message: string, type: MessageType): void {
        this.messages.add(new Message(message, type));
    }
}
